// components/Nav.jsx
'use client'
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import Link from 'next/link'
import LinkComponent from '@components/LinkComponent'
import { useLocales } from '@context/LocaleProvider'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { setCookie, getCookie } from 'cookies-next'
import { FA_ICON_MAP } from '@components/shared/faIconMap'
import UserMenu from '@components/Auth/UserMenu'

// ─── Icon rendering ───────────────────────────────────────────────────────────
// Accepts the same shapes the admin navbar editor can save:
//   - a plain string: either a FA_ICON_MAP key ("FaHome") or a legacy emoji ("🏠")
//   - an object: { type: 'fa'|'emoji', value, color, size }
// Falls back to rendering the raw string (emoji) if the key isn't in the map,
// so nothing silently disappears if the map ever gets out of sync.
function hasIcon(icon) {
  if (!icon) return false
  if (typeof icon === 'string') return icon.trim().length > 0
  if (!icon.type || icon.type === 'none') return false
  return !!(icon.value && String(icon.value).trim())
}

function RenderIcon({ icon, size = 14, color, style, gap = 6 }) {
  if (!hasIcon(icon)) return null

  if (typeof icon === 'string') {
    const Icon = FA_ICON_MAP[icon]
    if (Icon) return <Icon size={size} color={color} style={{ flexShrink: 0, marginInlineEnd: gap, ...style }} />
    return <span style={{ fontSize: size, lineHeight: 1, marginInlineEnd: gap, ...style }}>{icon}</span>
  }

  const iconSize = icon.size ?? size
  const iconColor = icon.color ? resolveColor(icon.color) : color

  if (icon.type === 'emoji') {
    return <span style={{ fontSize: iconSize, lineHeight: 1, marginInlineEnd: gap, ...style }}>{icon.value}</span>
  }
  if (icon.type === 'fa') {
    const Icon = FA_ICON_MAP[icon.value]
    if (!Icon) return null
    return <Icon size={iconSize} color={iconColor} style={{ flexShrink: 0, marginInlineEnd: gap, ...style }} />
  }
  return null
}

// ─── RTL helpers ──────────────────────────────────────────────────────────────

const RTL_LANGS  = new Set(['he', 'ar', 'fa', 'ur'])
const isRTL      = (lang) => RTL_LANGS.has(lang)

// ─── Safe label reader ────────────────────────────────────────────────────────
function getLabel(label, lang) {
  if (!label) return ''
  if (typeof label.get === 'function') return label.get(lang) ?? label.get('en') ?? ''
  return label[lang] ?? label['en'] ?? ''
}

// ─── CSS variable resolver ────────────────────────────────────────────────────
const CSS_VAR_FALLBACKS = {
  'var(--color-main)':      '#019da4',
  'var(--color-secondary)': '#6b7280',
  'var(--color-price)':     '#019da4',
  'var(--thirdcolor)':      '#228be6',
  'var(--fourthcolor)':     '#d4d7d9',
  'var(--iconcolor)':       '#4f46e5',
  'var(--textcolor)':       '#374151',
}

function resolveColor(value) {
  if (!value) return value
  if (value.startsWith('#') || value.startsWith('rgb') || value === 'transparent') return value
  if (value.startsWith('var(') && typeof window !== 'undefined') {
    const name = value.slice(4, -1).trim()
    const computed = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    if (computed) return computed
  }
  return CSS_VAR_FALLBACKS[value] ?? value
}

// ─── Account item helpers ─────────────────────────────────────────────────────
// The account (login / avatar) control can come from either:
//   - a navbar item with type 'account' (its `url` is used as the profile link), or
//   - navbar.showAccount (ON by default — set false to hide) + navbar.accountPosition / navbar.accountUrl
function getAccountItem(navbar) {
  return (navbar?.items ?? []).find(i => i.type === 'account') ?? null
}
function getAccountProfileUrl(navbar) {
  return getAccountItem(navbar)?.url ?? navbar?.accountUrl
}

// ─── Hover hook ───────────────────────────────────────────────────────────────
function useHover() {
  const [hovered, setHovered] = useState(false)
  return [hovered, { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }]
}

// ─── Chevron icon ─────────────────────────────────────────────────────────────
function ChevronDown({ size = 10, style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" style={style}>
      <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Burger / close icon ──────────────────────────────────────────────────────
function BurgerIcon({ open, color }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
      {open ? (
        <path d="M5 5l12 12M17 5L5 17" stroke={color} strokeWidth="2" strokeLinecap="round" />
      ) : (
        <>
          <rect x="2" y="5"  width="18" height="2" rx="1" fill={color} />
          <rect x="2" y="10" width="18" height="2" rx="1" fill={color} />
          <rect x="2" y="15" width="18" height="2" rx="1" fill={color} />
        </>
      )}
    </svg>
  )
}

// ─── Dropdown (desktop) ───────────────────────────────────────────────────────
function DropdownMenu({ item, navbar, lang, pathname }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const [triggerHovered, triggerHoverProps] = useHover()
  const isMega = (item.dropdownStyle ?? 'simple') === 'mega'

  const isActive = item.children?.some(c => pathname === c.url || pathname?.startsWith(c.url + '/'))

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const linkColor  = resolveColor(navbar.linkColor       ?? '#374151')
  const hoverColor = resolveColor(navbar.linkHoverColor  ?? 'var(--color-main)')
  const dropBg     = resolveColor(navbar.dropdownBg      ?? '#ffffff')
  const dropRadius = navbar.dropdownRadius  ?? 8
  const dropShadow = navbar.dropdownShadow  ?? '0 8px 24px rgba(0,0,0,0.12)'
  const itemColor  = resolveColor(navbar.dropdownItemColor ?? '#374151')
  const hasDivider = navbar.dropdownDivider ?? false
  const indicator  = navbar.activeIndicator ?? 'underline'
  const fontSize   = navbar.linkFontSize    ?? 0.9375
  const fontWeight = navbar.linkFontWeight  ?? '500'
  const triggerColor = (isActive || triggerHovered) ? hoverColor : linkColor

  return (
    <div ref={ref} style={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}>
      <button
        {...triggerHoverProps}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: `${fontSize}rem`, fontWeight,
          color: triggerColor,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px 2px', position: 'relative',
          transition: 'color 0.15s',
        }}
      >
        {hasIcon(item.icon) && item.iconPosition !== 'right' && <RenderIcon icon={item.icon} color={triggerColor} />}
        {getLabel(item.label, lang)}
        {hasIcon(item.icon) && item.iconPosition === 'right' && <RenderIcon icon={item.icon} color={triggerColor} gap={0} style={{ marginInlineStart: 6 }} />}
        <ChevronDown style={{ opacity: 0.55, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        {isActive && indicator === 'underline' && (
          <span style={{ position: 'absolute', bottom: -2, left: 0, right: 0, height: 2, borderRadius: 2, background: hoverColor }} />
        )}
        {isActive && indicator === 'dot' && (
          <span style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: hoverColor }} />
        )}
      </button>

      {open && (isMega ? (
        <MegaDropdownPanel item={item} navbar={navbar} lang={lang} dropBg={dropBg} dropRadius={dropRadius} dropShadow={dropShadow} itemColor={itemColor} hoverColor={hoverColor} />
      ) : (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)',
          // insetInlineStart keeps the dropdown flush with the trigger's
          // "start" edge in both LTR and RTL
          insetInlineStart: 0,
          zIndex: 1000,
          background: dropBg, boxShadow: dropShadow, borderRadius: dropRadius,
          minWidth: 200, padding: '6px 0',
          animation: 'navDropFadeIn 0.12s ease',
        }}>
          {/* Invisible bridge covering the 8px gap */}
          <div style={{ position: 'absolute', top: -8, left: 0, right: 0, height: 8 }} />
          {(item.children ?? []).map((child, ci) => (
            <DropdownChildLink
              key={child.id ?? ci}
              child={child} lang={lang} pathname={pathname}
              hoverColor={hoverColor} itemColor={itemColor}
              fontSize={fontSize} hasDivider={hasDivider}
              isLast={ci === item.children.length - 1}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// ─── Mega menu panel (desktop) ─────────────────────────────────────────────────
function MegaDropdownPanel({ item, navbar, lang, dropBg, dropRadius, dropShadow, itemColor, hoverColor }) {
  const intro   = item.megaIntro ?? {}
  const columns = item.megaColumns ?? []
  const width   = item.megaWidth ?? 640
  const align   = item.megaAlign ?? 'left'
  const posStyle = align === 'right' ? { insetInlineEnd: 0 } : align === 'center' ? { insetInlineStart: '50%', transform: 'translateX(-50%)' } : { insetInlineStart: 0 }

  return (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', ...posStyle, zIndex: 1000,
      width, maxWidth: '92vw',
      background: dropBg, boxShadow: dropShadow, borderRadius: dropRadius,
      padding: 28, display: 'flex', gap: 36,
      animation: 'navDropFadeIn 0.12s ease',
    }}>
      <div style={{ position: 'absolute', top: -8, left: 0, right: 0, height: 8 }} />
      <div style={{ width: 220, flexShrink: 0 }}>
        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: itemColor, lineHeight: 1.25, marginBottom: 10 }}>
          {getLabel(intro.title, lang)}
        </div>
        {getLabel(intro.description, lang) && (
          <div style={{ fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5, marginBottom: 16 }}>
            {getLabel(intro.description, lang)}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(intro.buttons ?? []).map(btn => {
            const filled = (btn.variant ?? 'filled') === 'filled'
            return (
              <LinkComponent key={btn.id} href={btn.url ?? '/'}
                target={btn.openNew ? '_blank' : undefined}
                style={{
                  textDecoration: 'none', textAlign: 'center', fontSize: '0.8125rem', fontWeight: 600,
                  padding: '9px 14px', borderRadius: 6,
                  background: filled ? hoverColor : 'transparent',
                  color: filled ? '#fff' : itemColor,
                  border: filled ? 'none' : '1.5px solid #e5e7eb',
                }}>
                {getLabel(btn.label, lang)} →
              </LinkComponent>
            )
          })}
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {columns.map(col => (
          <div key={col.id} style={{ minWidth: 130 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: itemColor, marginBottom: 10 }}>
              {getLabel(col.title, lang)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(col.links ?? []).map(link => (
                <LinkComponent key={link.id} href={link.url ?? '/'}
                  target={link.openNew ? '_blank' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center',
                    textDecoration: 'none', fontSize: '0.8125rem', color: '#6b7280', whiteSpace: 'nowrap',
                  }}>
                  <RenderIcon icon={link.icon} color="#6b7280" />
                  {getLabel(link.label, lang)}
                </LinkComponent>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function DropdownChildLink({ child, lang, pathname, hoverColor, itemColor, fontSize, hasDivider, isLast }) {
  const [hovered, hoverProps] = useHover()
  const isActive = pathname === child.url
  const color = (isActive || hovered) ? hoverColor : itemColor
  return (
    <div {...hoverProps} style={{ borderBottom: hasDivider && !isLast ? '1px solid #f0f0f0' : 'none' }}>
      <LinkComponent
        href={child.url ?? '/'}
        target={child.openNew ? '_blank' : undefined}
        rel={child.openNew ? 'noopener noreferrer' : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 18px',
          fontSize: `${fontSize * 0.95}rem`, fontWeight: '400',
          color,
          textDecoration: 'none',
          transition: 'color 0.1s, background 0.1s',
          background: (isActive || hovered) ? `color-mix(in srgb, ${hoverColor} 8%, transparent)` : 'transparent',
        }}
      >
        <RenderIcon icon={child.icon} color={color} gap={0} />
        {getLabel(child.label, lang)}
      </LinkComponent>
    </div>
  )
}

// ─── Single desktop nav item ──────────────────────────────────────────────────
function DesktopNavItem({ item, navbar, lang, pathname }) {
  const [hovered, hoverProps] = useHover()

  if (item.type === 'divider') {
    return <div style={{ width: 1, height: 18, background: 'rgba(0,0,0,0.12)', flexShrink: 0 }} />
  }
  if (item.type === 'dropdown') {
    return <DropdownMenu item={item} navbar={navbar} lang={lang} pathname={pathname} />
  }

  const linkColor  = resolveColor(navbar.linkColor      ?? '#374151')
  const hoverColor = resolveColor(navbar.linkHoverColor ?? 'var(--color-main)')
  const fontSize   = navbar.linkFontSize   ?? 0.9375
  const fontWeight = navbar.linkFontWeight ?? '500'
  const indicator  = navbar.activeIndicator ?? 'underline'
  const isActive   = pathname === item.url || (item.url !== '/' && pathname?.startsWith(item.url + '/'))

  if (item.type === 'button') {
    const bc = item.btnColor ?? 'var(--color-main)'
    const variant = item.btnVariant ?? 'filled'
    let btnStyle = {}
    if (variant === 'filled')   btnStyle = { background: bc, color: item.btnTextColor ?? '#ffffff', border: 'none' }
    if (variant === 'outlined') btnStyle = { background: 'transparent', color: bc, border: `1.5px solid ${bc}` }
    if (variant === 'ghost')    btnStyle = { background: 'transparent', color: bc, border: 'none' }
    return (
      <div {...hoverProps} style={{ display: 'inline-flex', flexShrink: 0 }}>
        <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined} rel={item.openNew ? 'noopener noreferrer' : undefined}
          style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: `${fontSize}rem`, fontWeight: '600', padding: '7px 18px', borderRadius: item.btnRadius ?? 6, textDecoration: 'none', whiteSpace: 'nowrap', transition: 'opacity 0.15s', opacity: hovered ? 0.85 : 1 }}>
          {hasIcon(item.icon) && item.iconPosition !== 'right' && <RenderIcon icon={item.icon} color={btnStyle.color} gap={0} />}
          {getLabel(item.label, lang)}
          {hasIcon(item.icon) && item.iconPosition === 'right' && <RenderIcon icon={item.icon} color={btnStyle.color} gap={0} />}
        </LinkComponent>
      </div>
    )
  }

  const linkStyleColor = (isActive || hovered) ? hoverColor : linkColor
  return (
    <div {...hoverProps} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined} rel={item.openNew ? 'noopener noreferrer' : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', fontSize: `${fontSize}rem`, fontWeight, color: linkStyleColor, textDecoration: 'none', padding: '4px 2px', whiteSpace: 'nowrap', position: 'relative', transition: 'color 0.15s' }}>
        {hasIcon(item.icon) && item.iconPosition !== 'right' && <RenderIcon icon={item.icon} color={linkStyleColor} />}
        {getLabel(item.label, lang)}
        {hasIcon(item.icon) && item.iconPosition === 'right' && <RenderIcon icon={item.icon} color={linkStyleColor} gap={0} style={{ marginInlineStart: 6 }} />}
        {isActive && indicator === 'underline' && (
          <span style={{ position: 'absolute', bottom: -2, left: 0, right: 0, height: 2, borderRadius: 2, background: hoverColor }} />
        )}
        {isActive && indicator === 'dot' && (
          <span style={{ position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: hoverColor }} />
        )}
      </LinkComponent>
    </div>
  )
}

// ─── Mobile drawer ────────────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, navbar, lang, pathname }) {
  const m          = navbar.mobile ?? {}
  const rtl        = isRTL(lang)
  // In RTL, flip the drawer to the opposite physical side so it still
  // appears at the visual "end" of the screen (left in RTL, right in LTR)
  const configSide = m.drawerSide ?? 'right'
  const drawerSide = rtl
    ? (configSide === 'right' ? 'left' : 'right')
    : configSide

  const drawerBg   = m.drawerBg          ?? '#ffffff'
  const drawerW    = m.drawerWidth        ?? 280
  const itemColor  = resolveColor(m.drawerItemColor   ?? '#374151')
  const itemSize   = m.drawerItemSize    ?? 1
  const itemWeight = m.drawerItemWeight  ?? '500'
  const itemPad    = m.drawerItemPadding ?? 14
  const hoverColor = resolveColor(navbar.linkHoverColor ?? 'var(--color-main)')

  // Login / account block in the drawer
  const showAccount = false /*!!getAccountItem(navbar) || navbar.showAccount !== false*/

  // "sub" screen — the full-screen slide-over shown for a tapped dropdown (simple or mega).
  const [subItemId, setSubItemId] = useState(null)
  const [megaColOpen, setMegaColOpen] = useState({})
  const subItem = subItemId ? (navbar.items ?? []).find(i => i.id === subItemId) : null

  const pathname_ = usePathname()
  useEffect(() => { onClose(); setSubItemId(null) }, [pathname_]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])
  useEffect(() => { if (!open) setSubItemId(null) }, [open])

  // Translate off-screen in the correct physical direction
  const translateOut = drawerSide === 'right' ? 'translateX(100%)' : 'translateX(-100%)'

  return (
    <>
      {open && m.drawerOverlay !== false && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', animation: 'navFadeIn 0.15s ease' }} />
      )}
      {/* dir="rtl" on the drawer itself so text inside flows correctly */}
      <div
        dir={rtl ? 'rtl' : 'ltr'}
        style={{
          position: 'fixed', top: 0, [drawerSide]: 0,
          width: drawerW, height: '100dvh',
          background: drawerBg, zIndex: 1001,
          display: 'flex', flexDirection: 'column',
          boxShadow: drawerSide === 'right' ? '-4px 0 32px rgba(0,0,0,0.14)' : '4px 0 32px rgba(0,0,0,0.14)',
          transform: open ? 'translateX(0)' : translateOut,
          transition: 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
          overflowY: 'auto',
        }}
      >
        {subItem ? (
          <MobileSubPanel item={subItem} navbar={navbar} lang={lang} itemColor={itemColor} hoverColor={hoverColor}
            itemPad={itemPad} itemSize={itemSize} itemWeight={itemWeight}
            megaColOpen={megaColOpen} setMegaColOpen={setMegaColOpen}
            onBack={() => setSubItemId(null)} />
        ) : (
          <>
            {/* Header — justifyContent:'space-between' already flips with dir */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: navbar.height ?? 68, borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
              <NavLogo navbar={navbar} lang={lang} />
              <button onClick={onClose} aria-label="Close menu" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: itemColor, lineHeight: 1 }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Account — login button or avatar + name + log out */}
            {showAccount && (
              <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
                <UserMenu variant="drawer" lang={lang}
                  linkColor={itemColor} hoverColor={hoverColor}
                  profileUrl={getAccountProfileUrl(navbar)} />
              </div>
            )}

            {/* Items */}
            <nav style={{ flex: 1, padding: '8px 0' }}>
              {(navbar.items ?? []).filter(i => i.type !== 'language' && i.type !== 'currency' && i.type !== 'account').map((item) => {
                if (item.type === 'divider') {
                  return <div key={item.id} style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '4px 16px' }} />
                }
                const isActive   = pathname === item.url || (item.url && item.url !== '/' && pathname?.startsWith(item.url + '/'))
                const isDropdown = item.type === 'dropdown'

                if (item.type === 'button') {
                  const bc = item.btnColor ?? 'var(--color-main)'
                  const variant = item.btnVariant ?? 'filled'
                  let btnStyle = {}
                  if (variant === 'filled')   btnStyle = { background: bc, color: item.btnTextColor ?? '#fff', border: 'none' }
                  if (variant === 'outlined') btnStyle = { background: 'transparent', color: bc, border: `1.5px solid ${bc}` }
                  if (variant === 'ghost')    btnStyle = { background: 'transparent', color: bc, border: 'none' }
                  return (
                    <div key={item.id} style={{ padding: '6px 16px' }}>
                      <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined}
                        style={{ ...btnStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textAlign: 'center', padding: '10px 0', borderRadius: item.btnRadius ?? 6, fontSize: `${itemSize}rem`, fontWeight: '600', textDecoration: 'none' }}>
                        <RenderIcon icon={item.icon} color={btnStyle.color} gap={0} />
                        {getLabel(item.label, lang)}
                      </LinkComponent>
                    </div>
                  )
                }

                return (
                  <div key={item.id}
                    onClick={() => isDropdown && setSubItemId(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: `${itemPad}px 20px`, cursor: 'pointer',
                      borderBottom: '1px solid rgba(0,0,0,0.04)',
                      color: isActive ? hoverColor : itemColor,
                      fontSize: `${itemSize}rem`, fontWeight: itemWeight,
                    }}>
                    {isDropdown ? (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <RenderIcon icon={item.icon} color={isActive ? hoverColor : itemColor} />
                        {getLabel(item.label, lang)}
                      </span>
                    ) : (
                      <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined}
                        style={{ display: 'flex', alignItems: 'center', color: 'inherit', textDecoration: 'none', flex: 1 }}>
                        <RenderIcon icon={item.icon} color={isActive ? hoverColor : itemColor} />
                        {getLabel(item.label, lang)}
                      </LinkComponent>
                    )}
                    {isDropdown && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#9ca3af', flexShrink: 0, transform: rtl ? 'scaleX(-1)' : 'none' }}>
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    )}
                  </div>
                )
              })}
            </nav>

            {/* Footer */}
            {(m.showLangInDrawer !== false || m.showCurrInDrawer !== false) && (
              <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', gap: 8, flexShrink: 0 }}>
                {m.showCurrInDrawer !== false && navbar.showCurrency && <CurrencySelector navbar={navbar} compact />}
                {m.showLangInDrawer !== false && navbar.showLanguage && <LanguageSelector lang={lang} navbar={navbar} compact />}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// Full-screen slide-over shown when a mobile drawer dropdown (simple or mega) is tapped.
function MobileSubPanel({ item, navbar, lang, itemColor, hoverColor, itemPad, itemSize, itemWeight, megaColOpen, setMegaColOpen, onBack }) {
  const isMega = (item.dropdownStyle ?? 'simple') === 'mega'
  const rtl = isRTL(lang)
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', height: navbar.height ?? 68, borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: itemColor, fontSize: 14, fontWeight: 600, padding: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: rtl ? 'scaleX(-1)' : 'none' }}>
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: isMega ? '18px 20px' : '8px 0' }}>
        {isMega ? (
          <>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: itemColor, lineHeight: 1.25, marginBottom: 10 }}>
              {getLabel(item.megaIntro?.title, lang)}
            </div>
            {getLabel(item.megaIntro?.description, lang) && (
              <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 18 }}>
                {getLabel(item.megaIntro?.description, lang)}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {(item.megaIntro?.buttons ?? []).map(btn => {
                const filled = (btn.variant ?? 'filled') === 'filled'
                return (
                  <LinkComponent key={btn.id} href={btn.url ?? '/'}
                    target={btn.openNew ? '_blank' : undefined}
                    style={{
                      textDecoration: 'none', textAlign: 'center', fontSize: 13.5, fontWeight: 600,
                      padding: '11px 14px', borderRadius: 6,
                      background: filled ? hoverColor : 'transparent',
                      color: filled ? '#fff' : itemColor,
                      border: filled ? 'none' : '1.5px solid #e5e7eb',
                    }}>
                    {getLabel(btn.label, lang)} →
                  </LinkComponent>
                )
              })}
            </div>
            {(item.megaColumns ?? []).map(col => {
              const isOpen = !!megaColOpen[col.id]
              return (
                <div key={col.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <div onClick={() => setMegaColOpen(s => ({ ...s, [col.id]: !s[col.id] }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 2px', cursor: 'pointer' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: itemColor }}>{getLabel(col.title, lang)}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ color: '#9ca3af', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </div>
                  {isOpen && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 12 }}>
                      {(col.links ?? []).map(link => (
                        <LinkComponent key={link.id} href={link.url ?? '/'}
                          target={link.openNew ? '_blank' : undefined}
                          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', fontSize: 13.5, color: '#6b7280', padding: '6px 2px' }}>
                          <RenderIcon icon={link.icon} color="#6b7280" />
                          {getLabel(link.label, lang)}
                        </LinkComponent>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </>
        ) : (
          (item.children ?? []).map(child => (
            <LinkComponent key={child.id} href={child.url ?? '/'} target={child.openNew ? '_blank' : undefined} style={{
              display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none',
              padding: `${itemPad}px 20px`, fontSize: `${itemSize}rem`, fontWeight: itemWeight,
              color: itemColor, borderBottom: '1px solid #f9fafb',
            }}>
              <RenderIcon icon={child.icon} color={itemColor} gap={0} />
              {getLabel(child.label, lang)}
            </LinkComponent>
          ))
        )}
      </div>
    </>
  )
}

// ─── Logo ─────────────────────────────────────────────────────────────────────
function NavLogo({ navbar, lang }) {
  const content = navbar.logoType === 'text' ? (
    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: navbar.linkColor ?? '#374151', whiteSpace: 'nowrap' }}>
      {getLabel(navbar.logoText, lang)}
    </span>
  ) : navbar.logoUrl ? (
    <Image src={navbar.logoUrl} alt="logo" width={navbar.logoWidth ?? 140} height={navbar.logoHeight ?? 40}
      style={{ objectFit: 'contain', width: navbar.logoWidth ?? 140, height: navbar.logoHeight ?? 40 }} priority />
  ) : (
    <span style={{ fontSize: '1.1rem', fontWeight: 700, color: navbar.linkColor ?? '#374151' }}>Logo</span>
  )
  return (
    <LinkComponent href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {content}
    </LinkComponent>
  )
}

// ─── Currency selector ────────────────────────────────────────────────────────
// Reads the persisted `currency` cookie on mount, writes it on change, and
// calls router.refresh() so server components re-read it.
function CurrencySelector({ navbar, compact = false, item = {} }) {
  const options  = ((item.currencyOptions ?? navbar.currencyOptions) ?? 'THB,USD,EUR,GBP').split(',').map(s => s.trim()).filter(Boolean)
  const router   = useRouter()
  const [open, setOpen]         = useState(false)
  const [selected, setSelected] = useState(options[0] ?? 'THB')
  const ref      = useRef(null)
  const linkColor  = resolveColor(navbar.linkColor      ?? '#374151')
  const hoverColor = resolveColor(navbar.linkHoverColor ?? 'var(--color-main)')

  const variant = item.selectorVariant ?? 'pill'
  const iconType = item.selectorIcon ?? 'globe'
  const tc  = resolveColor(item.selectorColor ?? linkColor)
  const bg  = variant === 'text' || variant === 'ghost' ? 'transparent'
             : variant === 'outlined' ? 'transparent'
             : (item.selectorBg ?? (compact ? '#f3f4f6' : 'rgba(0,0,0,0.05)'))
  const border = variant === 'outlined' ? `1.5px solid ${tc}` : 'none'
  const radius = item.selectorRadius ?? (variant === 'pill' ? 999 : 6)

  const currIcon = iconType === 'globe' ? (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/>
    </svg>
  ) : null

  useEffect(() => {
    const saved = getCookie('currency')
    if (!saved) return
    const match = options.find(o => o.toLowerCase() === String(saved).toLowerCase())
    if (match) setSelected(match)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const switchTo = (o) => {
    const code = o.toLowerCase()
    setCookie('currency', code)
    setSelected(o)
    setOpen(false)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('currencychange', { detail: { currency: code } }))
    }
    router.refresh()
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: compact ? '0.8rem' : '0.8125rem', fontWeight: 600, color: tc, background: bg, border, cursor: 'pointer', padding: '5px 10px', borderRadius: radius }}>
        {currIcon}
        <span>{selected}</span>
        <ChevronDown style={{ opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: compact ? '110%' : 'auto', top: compact ? 'auto' : '110%',
          insetInlineStart: 0,
          zIndex: 1000,
          background: resolveColor(item.dropdownBg ?? '#ffffff'),
          borderRadius: item.dropdownRadius ?? 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          minWidth: 90, padding: '4px 0',
          animation: 'navDropFadeIn 0.1s ease',
        }}>
          {options.map(o => (
            <CurrencyOption key={o} label={o} selected={selected === o}
              activeColor={resolveColor(item.dropdownActiveColor ?? hoverColor)}
              textColor={resolveColor(item.dropdownTextColor ?? '#374151')}
              onClick={() => switchTo(o)} />
          ))}
        </div>
      )}
    </div>
  )
}

function CurrencyOption({ label, selected, activeColor, textColor, onClick }) {
  const [hovered, hoverProps] = useHover()
  return (
    <button onClick={onClick} {...hoverProps}
      style={{
        display: 'block', width: '100%', padding: '7px 14px',
        textAlign: 'start',
        fontSize: '0.8125rem', fontWeight: selected ? 700 : 400,
        color: (selected || hovered) ? activeColor : textColor,
        background: hovered ? `color-mix(in srgb, ${activeColor} 6%, transparent)` : 'none',
        border: 'none', cursor: 'pointer', transition: 'color 0.1s',
      }}>
      {label}
    </button>
  )
}

// ─── Language selector ────────────────────────────────────────────────────────
const LANG_FULL   = { en: 'English', th: 'ไทย', cn: '中文', ja: '日本語', ru: 'Русский', de: 'Deutsch', fr: 'Français', it: 'Italiano', es: 'Español', he: 'עברית', ar: 'العربية' }
const LANG_LABELS = {
  en: 'English',
  th: 'ไทย',
  cn: '中文',
  zh: '中文',
  'zh-tw': '繁中',
  ja: '日本語',
  ko: '한국어',
  ru: 'Рус',
  de: 'DE',
  it: 'IT',
  fr: 'FR',
  es: 'ES',
  he: 'עב',
  ar: 'عربي',
}

// Returns the locale encoded in the URL, or the default locale for unprefixed paths.
function localeFromPath(pathname, locales, defaultLocale) {
  const seg = (pathname ?? '/').split('/')[1]
  return locales.includes(seg) ? seg : defaultLocale
}

// Builds the URL for `locale` from the current path.
// The default locale is served WITHOUT a prefix (/about), others WITH one (/th/about).
function pathForLocale(pathname, locale, locales, defaultLocale) {
  if (!pathname) return '/'
  const segments = pathname.split('/')
  const hasPrefix = locales.includes(segments[1])
  const rest = hasPrefix ? '/' + segments.slice(2).join('/') : pathname
  const clean = rest === '' ? '/' : rest
  if (locale === defaultLocale) return clean
  return `/${locale}${clean === '/' ? '' : clean}`
}

function LanguageSelector({ lang, navbar, compact = false, item = {} }) {
  // Real list from the server (via LocaleProvider), not the static fallback
  const { locales, defaultLocale } = useLocales()
  const router   = useRouter()
  const pathname = usePathname()
  const ref      = useRef(null)
  const [open, setOpen]         = useState(false)
  const [selected, setSelected] = useState(lang ?? localeFromPath(pathname, locales, defaultLocale))
  const linkColor  = resolveColor(navbar.linkColor      ?? '#374151')
  const hoverColor = resolveColor(navbar.linkHoverColor ?? 'var(--color-main)')

  const variant     = item.selectorVariant     ?? 'pill'
  const iconType    = item.selectorIcon        ?? 'globe'
  const labelFormat = item.selectorLabelFormat ?? 'code'
  const tc  = resolveColor(item.selectorColor ?? linkColor)
  const bg  = variant === 'text' || variant === 'ghost' ? 'transparent'
             : variant === 'outlined' ? 'transparent'
             : (item.selectorBg ?? (compact ? '#f3f4f6' : 'rgba(0,0,0,0.05)'))
  const border = variant === 'outlined' ? `1.5px solid ${tc}` : 'none'
  const radius = item.selectorRadius ?? (variant === 'pill' ? 999 : 6)

  const globeIcon = iconType === 'globe' ? (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/>
    </svg>
  ) : iconType === 'world' ? (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
    </svg>
  ) : null

  const displayLabel = labelFormat === 'full'
    ? (LANG_FULL[selected] ?? selected?.toUpperCase())
    : labelFormat === 'icon' ? null
    : (LANG_LABELS[selected] ?? selected?.toUpperCase() ?? 'EN')

  // Keep the NEXT_LOCALE cookie in sync with the URL — including unprefixed
  // (default-language) pages. Without this, a stale cookie like "th" can make
  // middleware bounce "/" to "/th" (or "/en").
  useEffect(() => {
    if (!pathname) return
    const urlLocale = localeFromPath(pathname, locales, defaultLocale)
    setSelected(urlLocale)
    if (getCookie('NEXT_LOCALE') !== urlLocale) {
      setCookie('NEXT_LOCALE', urlLocale)
      router.refresh()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const switchTo = (locale) => {
    setCookie('NEXT_LOCALE', locale)
    setSelected(locale)
    setOpen(false)
    // router.refresh()
  }

  if (locales.length <= 1) return null

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: compact ? '0.8rem' : '0.8125rem', fontWeight: 600, color: tc, background: bg, border, cursor: 'pointer', padding: '5px 10px', borderRadius: radius }}>
        {globeIcon}
        {displayLabel && <span>{displayLabel}</span>}
        <ChevronDown style={{ opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: compact ? '110%' : 'auto', top: compact ? 'auto' : '110%',
          insetInlineEnd: 0,
          zIndex: 1000,
          background: resolveColor(item.dropdownBg ?? '#ffffff'),
          borderRadius: item.dropdownRadius ?? 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          minWidth: 100, padding: '4px 0',
          animation: 'navDropFadeIn 0.1s ease',
        }}>
          {locales.map(locale => (
            <LangOption key={locale} locale={locale} selected={selected === locale}
              activeColor={resolveColor(item.dropdownActiveColor ?? hoverColor)}
              textColor={resolveColor(item.dropdownTextColor ?? '#374151')}
              labelFormat={labelFormat} href={pathForLocale(pathname, locale, locales, defaultLocale)} onClick={() => switchTo(locale)} />
          ))}
        </div>
      )}
    </div>
  )
}

function LangOption({ locale, selected, activeColor, textColor, href, onClick, labelFormat }) {
  const [hovered, hoverProps] = useHover()
  const label = labelFormat === 'full'
    ? (LANG_FULL[locale] ?? locale.toUpperCase())
    : (LANG_LABELS[locale] ?? locale.toUpperCase())
  // Plain next/link on purpose: href is already the final, locale-correct path,
  // so it must NOT go through LinkComponent (which would re-prefix it).
  return (
    <a href={href} onClick={onClick} {...hoverProps}
      style={{ display: 'block', padding: '7px 16px', textAlign: 'center', fontSize: '0.8125rem', fontWeight: selected ? 700 : 400, color: (selected || hovered) ? activeColor : textColor, textDecoration: 'none', background: (selected || hovered) ? `color-mix(in srgb, ${activeColor} 8%, transparent)` : 'none', borderRadius: 6, transition: 'color 0.1s', whiteSpace: 'nowrap' }}>
      {label}
    </a>
  )
}

// ─── Top bar (secondary / utility bar — dual navbar) ───────────────────────────
function TopBar({ navbar, lang, insetLeft = 24, insetRight = 24 }) {
  const tb = navbar.topBar
  if (!tb?.enabled) return null

  const bg         = resolveColor(tb.bgColor ?? '#000000')
  const tc         = resolveColor(tb.textColor ?? '#ffffff')
  const hc         = resolveColor(tb.hoverColor ?? 'var(--color-main)')
  const height     = tb.height ?? 40
  const gap        = tb.itemGap ?? 20
  const sticky     = tb.stickyMode ?? 'both'
  const breakpoint = navbar?.mobile?.breakpoint ?? 1024

  const itemsBySlot = (slot) => (tb.items ?? []).filter(i => (i.slot ?? 'right') === slot)

  const wrapperStyle = sticky === 'both'
    ? { position: 'sticky', top: 0, zIndex: 101 }
    : {}

  return (
    <nav className="nav-topbar" aria-label="Utility navigation" style={{
      ...wrapperStyle,
      background: bg,
      height,
      borderBottom: tb.borderBottom ? `1px solid ${resolveColor(tb.borderColor ?? '#1f2937')}` : 'none',
      width: '100%',
    }}>
      {/* overflow-x:auto only on the mobile condensed bar — on desktop it would
          force overflow-y:auto too and clip the dropdown flyouts. */}
      <style>{`
        .nav-topbar-content { overflow: visible; }
        @media (max-width: ${breakpoint - 1}px) {
          .nav-topbar-content { overflow-x: auto; overflow-y: visible; }
        }
      `}</style>
      {/* Padded to the measured edges of the main nav content so both bars line up. */}
      <div className="nav-topbar-content" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: '100%', width: '100%', gap: 12,
        paddingLeft: insetLeft, paddingRight: insetRight,
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap }}>{itemsBySlot('left').map(item => <TopBarItem key={item.id} item={item} lang={lang} textColor={tc} hoverColor={hc} />)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap }}>{itemsBySlot('center').map(item => <TopBarItem key={item.id} item={item} lang={lang} textColor={tc} hoverColor={hc} />)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap }}>{itemsBySlot('right').map(item => <TopBarItem key={item.id} item={item} lang={lang} textColor={tc} hoverColor={hc} />)}</div>
      </div>
    </nav>
  )
}

function TopBarItem({ item, lang, textColor, hoverColor }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const [hovered, hoverProps] = useHover()

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  if (item.type === 'divider') {
    return <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.25)' }} />
  }

  const baseStyle = {
    fontSize: '0.8125rem', fontWeight: 400, color: textColor,
    textDecoration: 'none', whiteSpace: 'nowrap',
    display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
  }

  if (item.type === 'language') return <LanguageSelector lang={lang} navbar={{ linkColor: textColor, linkHoverColor: hoverColor }} item={item} compact />
  if (item.type === 'currency') return <CurrencySelector navbar={{ linkColor: textColor, linkHoverColor: hoverColor }} item={item} compact />
  if (item.type === 'account')  return <UserMenu lang={lang} linkColor={textColor} hoverColor={hoverColor} profileUrl={item.url} />

  if (item.type === 'button') {
    const bc = item.btnColor ?? textColor
    let btnStyle = {}
    if (item.btnVariant === 'filled')   btnStyle = { background: bc, color: item.btnTextColor ?? '#fff', border: 'none' }
    if (item.btnVariant === 'outlined') btnStyle = { background: 'transparent', color: item.btnTextColor ?? textColor, border: `1.5px solid ${item.btnTextColor ?? textColor}` }
    if (item.btnVariant === 'ghost')    btnStyle = { background: 'transparent', color: item.btnTextColor ?? textColor, border: 'none' }
    return (
      <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined}
        style={{ ...btnStyle, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8125rem', fontWeight: 600, padding: '4px 10px', borderRadius: item.btnRadius ?? 6, textDecoration: 'none', whiteSpace: 'nowrap' }}>
        {hasIcon(item.icon) && item.iconPosition !== 'right' && <RenderIcon icon={item.icon} color={btnStyle.color} gap={0} />}
        {getLabel(item.label, lang)}
        {hasIcon(item.icon) && item.iconPosition === 'right' && <RenderIcon icon={item.icon} color={btnStyle.color} gap={0} />}
      </LinkComponent>
    )
  }

  if (item.type === 'dropdown') {
    return (
      <div ref={ref} style={{ position: 'relative' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <span onClick={() => setOpen(o => !o)} style={{ ...baseStyle, color: hovered ? hoverColor : textColor }} {...hoverProps}>
          <RenderIcon icon={item.icon} color={hovered ? hoverColor : textColor} />
          {getLabel(item.label, lang)}
          <svg width="8" height="8" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.6 }}>
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {open && (item.children ?? []).length > 0 && (
          <>
            {/* Invisible bridge over the 6px gap so mouseleave doesn't close the menu */}
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: 6 }} />
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', insetInlineStart: 0, zIndex: 1000, background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', borderRadius: 6, minWidth: 140, padding: '4px 0' }}>
              {(item.children ?? []).map((child, ci) => (
                <LinkComponent key={child.id ?? ci} href={child.url ?? '/'}
                  target={child.openNew ? '_blank' : undefined}
                  style={{ display: 'flex', alignItems: 'center', padding: '6px 14px', textDecoration: 'none', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                  <RenderIcon icon={child.icon} color="#374151" />
                  {getLabel(child.label, lang)}
                </LinkComponent>
              ))}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <LinkComponent href={item.url ?? '/'} target={item.openNew ? '_blank' : undefined} {...hoverProps}
      style={{ ...baseStyle, color: hovered ? hoverColor : textColor }}>
      <RenderIcon icon={item.icon} color={hovered ? hoverColor : textColor} />
      {getLabel(item.label, lang)}
    </LinkComponent>
  )
}

// ─── Bottom nav icons ─────────────────────────────────────────────────────────

function BottomNavIcon({ icon, size }) {
  const fillPaths = {
    home:      'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
    star:      'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
    heart:     'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z',
    whatsapp:  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z',
    facebook:  'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z',
    tiktok:    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
    line:      'M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314',
    wechat:    'M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c-.303-.76-.464-1.569-.464-2.41 0-3.71 3.497-6.715 7.809-6.715.28 0 .558.013.833.039C15.936 4.745 12.662 2.188 8.691 2.188m-2.223 3.988a1.008 1.008 0 1 1 0 2.016 1.008 1.008 0 0 1 0-2.016m4.447 0a1.008 1.008 0 1 1 0 2.016 1.008 1.008 0 0 1 0-2.016M24 13.778c0-3.41-3.222-6.179-7.2-6.179-3.978 0-7.2 2.77-7.2 6.179s3.222 6.18 7.2 6.18c.78 0 1.539-.108 2.256-.302a.68.68 0 0 1 .564.08l1.5.88a.26.26 0 0 0 .131.044.231.231 0 0 0 .229-.23c0-.058-.023-.115-.038-.172l-.308-1.167a.47.47 0 0 1 .168-.526C23.01 17.607 24 15.786 24 13.778m-9.6-1.008a1.008 1.008 0 1 1 0-2.016 1.008 1.008 0 0 1 0 2.016m4.8 0a1.008 1.008 0 1 1 0-2.016 1.008 1.008 0 0 1 0 2.016',
  }

  const strokeContent = {
    search:   <><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></>,
    mail:     <><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    phone:    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.55 5.55l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>,
    user:     <><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></>,
    menu:     <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>,
    building: <><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/></>,
    globe:    <><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><path d="M2 12h20"/></>,
    key:      <><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></>,
  }

  if (fillPaths[icon]) {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d={fillPaths[icon]}/></svg>
  }
  if (strokeContent[icon]) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {strokeContent[icon]}
      </svg>
    )
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="4"/></svg>
}

const CONTACT_BRAND_COLORS = {
  whatsapp: '#25D366', line: '#06C755', wechat: '#07C160',
  phone: '#22c55e', email: '#6b7280', facebook: '#1877F2',
  instagram: '#E1306C', tiktok: '#010101',
}

// ─── Bottom nav component ─────────────────────────────────────────────────────
function BottomNav({ navbar, lang, pathname }) {
  const bn = navbar?.bottomNav
  if (!bn?.enabled || !bn?.items?.length) return null

  const bg            = bn.bg           ?? '#ffffff'
  const activeColor   = resolveColor(bn.activeColor  ?? 'var(--color-main)')
  const inactiveColor = bn.inactiveColor ?? '#9ca3af'
  const iconSize      = bn.iconSize     ?? 22
  const labelSize     = bn.labelSize    ?? 11
  const showLabels    = bn.showLabels   ?? true
  const paddingY      = bn.paddingY     ?? 8

  const getItemColor = (item, isActive) => {
    if (isActive) return activeColor
    const ct   = item.contactType
    const mode = item.contactColorMode ?? 'brand'
    if (ct && ct !== 'none') {
      if (mode === 'brand')    return CONTACT_BRAND_COLORS[ct] ?? inactiveColor
      if (mode === 'custom')   return item.contactCustomColor ?? inactiveColor
      if (mode === 'inactive') return inactiveColor
    }
    return inactiveColor
  }

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
      background: bg,
      borderTop: bn.borderTop ? `1px solid ${bn.borderColor ?? '#e5e7eb'}` : 'none',
      display: 'flex',
      padding: `${paddingY}px 0`,
      paddingBottom: `calc(${paddingY}px + env(safe-area-inset-bottom, 0px))`,
    }}>
      {bn.items.map(item => {
        const isActive = item.matchExact
          ? pathname === item.url
          : (item.url && item.url !== '/' ? pathname?.startsWith(item.url) : pathname === item.url || pathname === '/')
        const color = getItemColor(item, isActive)
        const isFa = (item.iconSource ?? 'builtin') === 'fa'

        const content = (
          <>
            {isFa ? (
              <RenderIcon icon={item.faIcon} size={Math.round(iconSize * 0.82)} color={color} gap={0} />
            ) : (
              <BottomNavIcon icon={item.icon} size={iconSize} />
            )}
            {showLabels && (
              <span style={{ fontSize: labelSize, fontWeight: isActive ? 700 : 400, lineHeight: 1, whiteSpace: 'nowrap' }}>
                {getLabel(item.label, lang)}
              </span>
            )}
          </>
        )

        const itemStyle = {
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 2, color, textDecoration: 'none',
          transition: 'color 0.15s',
        }

        // LinkComponent now detects external URLs (wa.me, https://, tel:, mailto:…)
        // itself; isExternal just forces a new tab.
        return (
          <LinkComponent key={item.id} href={item.url ?? '/'} style={itemStyle}
            target={item.isExternal ? '_blank' : undefined}>
            {content}
          </LinkComponent>
        )
      })}
    </div>
  )
}

// ─── Main Nav component ───────────────────────────────────────────────────────
export default function Nav({ navbar, lang, transparent = false, isViewPage = false }) {

  const pathname   = usePathname()
  const [scrolled,   setScrolled]   = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const breakpoint = navbar?.mobile?.breakpoint ?? 1024
  const rtl        = isRTL(lang)

  // Measures the real rendered left/right edges of the main nav content so the
  // top bar can pad itself to match pixel for pixel.
  const navContentRef = useRef(null)
  const [navInset, setNavInset] = useState({ left: 24, right: 24 })

  useLayoutEffect(() => {
    const measure = () => {
      const el = navContentRef.current
      if (!el || window.innerWidth < breakpoint) return
      const rect = el.getBoundingClientRect()
      setNavInset({
        left:  Math.max(0, Math.round(rect.left)),
        right: Math.max(0, Math.round(window.innerWidth - rect.right)),
      })
    }
    measure()
    window.addEventListener('resize', measure)
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(measure))
    let ro
    if (typeof ResizeObserver !== 'undefined' && navContentRef.current) {
      ro = new ResizeObserver(measure)
      ro.observe(navContentRef.current)
    }
    return () => {
      window.removeEventListener('resize', measure)
      cancelAnimationFrame(raf1)
      if (ro) ro.disconnect()
    }
  }, [breakpoint])

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', handler, { passive: true })
    handler()
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    const handler = () => { if (window.innerWidth >= breakpoint) setMobileOpen(false) }
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [breakpoint])

  if (!navbar) return null

  const isTransparentMode = transparent && navbar.transparent !== false
  const useTransparentBg  = isTransparentMode && !scrolled && !mobileOpen

  const bgColor    = useTransparentBg
    ? 'transparent'
    : (scrolled && isTransparentMode ? (navbar.bgScrollColor ?? '#ffffff') : (navbar.bgColor ?? '#ffffff'))
  const borderProp = navbar.borderBottom && !useTransparentBg ? `1px solid ${navbar.borderColor ?? '#e5e7eb'}` : 'none'
  const shadowProp = useTransparentBg ? 'none' : (navbar.shadow ?? 'none')
  const height     = navbar.height ?? 68
  const layout     = navbar.layout ?? 'standard'
  const gap        = navbar.linkSpacing ?? 20

  const linkColorOverride  = useTransparentBg ? (navbar.transparentLinkColor ?? navbar.linkColor) : navbar.linkColor
  const navbarWithOverride = { ...navbar, linkColor: linkColorOverride }

  const itemsBySlot = (slot) => (navbar.items ?? []).filter(i => (i.slot ?? 'center') === slot)

  const renderZone = (slot) => {
    const items = itemsBySlot(slot)
    const n = navbarWithOverride
    const hasLangItem    = (navbar.items ?? []).some(i => i.type === 'language')
    const hasCurrItem    = (navbar.items ?? []).some(i => i.type === 'currency')
    const hasAccountItem = !!getAccountItem(navbar)
    const accountProps = {
      lang,
      linkColor:  n.linkColor ?? '#374151',
      hoverColor: n.linkHoverColor ?? 'var(--color-main)',
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap }}>
        {items.map(item => {
          if (item.type === 'language') return <LanguageSelector key={item.id} lang={lang} navbar={n} item={item} />
          if (item.type === 'currency') return <CurrencySelector key={item.id} navbar={n} item={item} />
          if (item.type === 'account')  return <UserMenu key={item.id} {...accountProps} profileUrl={item.url} />
          return <DesktopNavItem key={item.id} item={item} navbar={n} lang={lang} pathname={pathname} />
        })}
        {!hasLangItem && navbar.showLanguage && (navbar.languagePosition ?? 'right') === slot && (
          <LanguageSelector lang={lang} navbar={n} />
        )}
        {!hasCurrItem && navbar.showCurrency && (navbar.currencyPosition ?? 'right') === slot && (
          <CurrencySelector navbar={n} />
        )}
        {/* {!hasAccountItem && navbar.showAccount !== false && (navbar.accountPosition ?? 'right') === slot && (
          <UserMenu {...accountProps} profileUrl={navbar.accountUrl} />
        )} */}
      </div>
    )
  }

  const renderDesktopLayout = () => {
    const leftZone   = renderZone('left')
    const centerZone = renderZone('center')
    const rightZone  = renderZone('right')

    if (layout === 'centered') return (
      <div className="container-wide" ref={navContentRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <NavLogo navbar={navbarWithOverride} lang={lang} />
          {leftZone}
        </div>
        {centerZone}
        <div style={{ flexShrink: 0 }}>{rightZone}</div>
      </div>
    )

    if (layout === 'split') return (
      <div className="container-wide" ref={navContentRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height, gap: 40 }}>
        {leftZone}
        <NavLogo navbar={navbarWithOverride} lang={lang} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{centerZone}{rightZone}</div>
      </div>
    )

    return (
      <div className="container-wide" ref={navContentRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavLogo navbar={navbarWithOverride} lang={lang} />
          {leftZone}
        </div>
        {centerZone}
        {rightZone}
      </div>
    )
  }

  const burgerColor = useTransparentBg
    ? (navbar.transparentBurgerColor ?? navbar.mobile?.burgerColor ?? '#374151')
    : (navbar.mobile?.burgerColor ?? '#374151')

  // Sticky behavior for the dual-navbar setup:
  //  - topBar.stickyMode 'both'         → TopBar sticky; main nav sticky below it.
  //  - topBar.stickyMode 'scrolls-away' → TopBar scrolls; only main nav sticky.
  //  - topBar.stickyMode 'none'         → neither is sticky.
  // Without a top bar, navbar.sticky controls the main nav.
  const tb = navbar.topBar
  const topBarEnabled = !!tb?.enabled
  const topBarHeight  = tb?.height ?? 40
  const topBarSticky  = topBarEnabled && (tb?.stickyMode ?? 'both') === 'both'
  const mainNavSticky = navbar.sticky !== false && (!topBarEnabled || (tb?.stickyMode ?? 'both') !== 'none')

  const navStickyStyle = mainNavSticky
    ? { position: 'sticky', top: topBarSticky ? topBarHeight : 0, zIndex: 100 }
    : {}

  return (
    <>
      <style>{`
        @keyframes navDropFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes navFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .nav-desktop { display: flex; }
        .nav-mobile-trigger { display: none; }
        .nav-topbar { display: flex; }
        @media (max-width: ${breakpoint - 1}px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-trigger { display: flex !important; }
          .nav-topbar { display: ${topBarEnabled && tb?.showOnMobile ? 'flex' : 'none'} !important; }
        }
      `}</style>

      <TopBar navbar={navbar} lang={lang} insetLeft={navInset.left} insetRight={navInset.right} />

      <nav
        dir={rtl ? 'rtl' : 'ltr'}
        style={{ ...navStickyStyle, background: bgColor, borderBottom: borderProp, boxShadow: shadowProp, transition: 'background 0.25s, box-shadow 0.25s, border-color 0.25s', width: '100%' }}
        aria-label="Main navigation"
      >
        <div className="nav-desktop" style={{ width: '100%' }}>
          {renderDesktopLayout()}
        </div>
        <div className="nav-mobile-trigger" style={{ width: '100%' }}>
          <div className="container-wide" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height, width: '100%', paddingInline: 16 }}>
            <NavLogo navbar={navbarWithOverride} lang={lang} />
            <button onClick={() => setMobileOpen(o => !o)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, lineHeight: 1 }}>
              <BurgerIcon open={mobileOpen} color={burgerColor} />
            </button>
          </div>
        </div>
      </nav>

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} navbar={navbarWithOverride} lang={lang} pathname={pathname} />

      <style>{`.nav-bottom-nav { display: block; } @media (min-width: ${breakpoint}px) { .nav-bottom-nav { display: none !important; } }`}</style>
      <div className="nav-bottom-nav">
        <BottomNav navbar={navbar} lang={lang} pathname={pathname} />
      </div>
    </>
  )
}